/**
 * Menu à onglets du header (style rhodeskin.com)
 *
 * Sans ce script, un seul panneau reste affiché et le menu fonctionne : tout ce
 * qui suit est de l'amélioration progressive (classe `is-enhanced`).
 *
 * Le panneau entrant glisse dans le sens de la navigation (animation CSS).
 * La hauteur n'est jamais calculée ici : les panneaux inactifs sortent du flux,
 * donc le menu fait exactement la hauteur de l'onglet affiché.
 */
(function () {
  'use strict';

  var SLIDE = 24; // amplitude du glissement, en px

  function MenuTabs(root) {
    this.root = root;
    this.nav = root.querySelector('[role="tablist"]');
    this.indicator = root.querySelector('[data-tab-indicator]');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab-index]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-panel-index]'));

    if (!this.tabs.length || !this.panels.length) return;

    root.__menuTabs = this;

    this.current = Math.max(
      0,
      this.panels.findIndex(function (p) {
        return p.classList.contains('is-active');
      })
    );

    this.init();
  }

  MenuTabs.prototype.init = function () {
    var self = this;

    this.panels.forEach(function (panel) {
      panel.removeAttribute('hidden');
    });
    this.root.classList.add('is-enhanced');

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        // Le focus explicite garde le drawer « actif » à ses propres yeux :
        // global.js le referme sur focusout si plus rien de lui n'a le focus.
        tab.focus({ preventScroll: true });
        self.select(parseInt(tab.getAttribute('data-tab-index'), 10));
      });
      tab.addEventListener('keydown', self.onKeydown.bind(self));
    });

    this.setupCarousels();

    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(function () {
        self.moveIndicator(false);
        self.updateArrows();
      });
      this.panels.forEach(function (panel) {
        self.observer.observe(panel);
      });
    }

    window.addEventListener('resize', this.onResize.bind(this));

    requestAnimationFrame(function () {
      self.moveIndicator(false);
      self.updateArrows();
    });
  };

  /* ---------- Carrousel horizontal (desktop) ---------- */

  MenuTabs.prototype.setupCarousels = function () {
    var self = this;

    this.carousels = this.panels.map(function (panel) {
      var track = panel.querySelector('[data-tab-track]');
      if (!track) return null;

      var prev = panel.querySelector('[data-tab-prev]');
      var next = panel.querySelector('[data-tab-next]');

      function scrollByPage(direction) {
        var card = track.querySelector('.menu-tabs__item');
        // Un cran = une carte entière ; à défaut, 80 % de la zone visible.
        var step = card ? card.offsetWidth + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      // Ce sont des <div role="button"> (voir menu-tabs.liquid) : le clavier
      // n'est pas géré nativement, on l'ajoute.
      function bindArrow(element, direction) {
        if (!element) return;
        element.addEventListener('click', function () {
          scrollByPage(direction);
        });
        element.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          scrollByPage(direction);
        });
      }

      bindArrow(prev, -1);
      bindArrow(next, 1);

      track.addEventListener('scroll', function () {
        self.updateArrows();
      });

      return { track: track, prev: prev, next: next };
    });
  };

  MenuTabs.prototype.updateArrows = function () {
    if (!this.carousels) return;

    var self = this;
    this.carousels.forEach(function (carousel, index) {
      if (!carousel) return;

      var track = carousel.track;
      // Marge de 2px : les sous-pixels de scrollWidth déclencheraient sinon
      // des flèches sur une liste qui tient pourtant entièrement à l'écran.
      var overflowing = track.scrollWidth - track.clientWidth > 2;

      track.classList.toggle('is-overflowing', overflowing);

      // Seul le panneau visible affiche ses flèches.
      var visible = overflowing && index === self.current;
      if (carousel.prev) carousel.prev.hidden = !visible || track.scrollLeft <= 2;
      if (carousel.next) {
        carousel.next.hidden =
          !visible || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }
    });
  };

  /* ---------- Onglets ---------- */

  MenuTabs.prototype.select = function (index) {
    if (isNaN(index) || index === this.current) return;
    if (!this.panels[index] || !this.tabs[index]) return;

    var direction = index > this.current ? 1 : -1;
    var outgoing = this.panels[this.current];
    var incoming = this.panels[index];

    incoming.style.setProperty('--mt-offset', direction * SLIDE + 'px');
    outgoing.style.setProperty('--mt-offset', -direction * SLIDE + 'px');

    // Reflow : sans lui, le navigateur fusionne l'état de départ et d'arrivée.
    void incoming.offsetWidth;

    outgoing.classList.remove('is-active');
    incoming.classList.add('is-active');

    this.tabs.forEach(function (tab, i) {
      var active = i === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.current = index;
    this.moveIndicator(true);
    this.scrollTabIntoView(index);
    this.updateArrows();
  };

  MenuTabs.prototype.moveIndicator = function (animate) {
    if (!this.indicator) return;
    var tab = this.tabs[this.current];
    if (!tab) return;

    if (!animate) this.indicator.style.transition = 'none';
    this.indicator.style.width = tab.offsetWidth + 'px';
    this.indicator.style.transform = 'translateX(' + tab.offsetLeft + 'px)';
    if (!animate) {
      void this.indicator.offsetWidth;
      this.indicator.style.transition = '';
    }
  };

  MenuTabs.prototype.scrollTabIntoView = function (index) {
    var tab = this.tabs[index];
    if (!tab || !this.nav || this.nav.scrollWidth <= this.nav.clientWidth) return;

    var target = tab.offsetLeft - (this.nav.clientWidth - tab.offsetWidth) / 2;
    this.nav.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  };

  MenuTabs.prototype.onKeydown = function (event) {
    var index = null;

    if (event.key === 'ArrowRight') index = this.current + 1;
    else if (event.key === 'ArrowLeft') index = this.current - 1;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = this.tabs.length - 1;
    else return;

    event.preventDefault();
    index = Math.max(0, Math.min(this.tabs.length - 1, index));
    this.select(index);
    this.tabs[index].focus({ preventScroll: true });
  };

  MenuTabs.prototype.onResize = function () {
    this.moveIndicator(false);
    this.updateArrows();
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-menu-tabs]').forEach(function (root) {
      if (root.dataset.menuTabsReady) return;
      root.dataset.menuTabsReady = 'true';
      new MenuTabs(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Éditeur de thème : sélectionner un bloc d'onglet ouvre l'onglet correspondant.
  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target.closest('[data-panel-index]');
    if (!panel) return;
    var root = panel.closest('[data-menu-tabs]');
    if (!root || !root.__menuTabs) return;
    root.__menuTabs.select(parseInt(panel.getAttribute('data-panel-index'), 10));
  });
})();
